<template>
    <div class="scroll-paginator-results">
		<div v-show="submitting && !scrollData.append" ref="skeletonUp" class="scroll-paginator-result-item">
			<slot name="skeletonLoader" />
		</div>
        <div v-for="(item, i) in rows" :key="item.id ? item.id : i" ref="rowElements" class="scroll-paginator-result-item">
            <slot name="item" :item="item" />
        </div>
		<div v-if="submitting && scrollData.append" ref="skeletonDown" class="scroll-paginator-result-item">
			<slot name="skeletonLoader" />
		</div>
    </div>
</template>

<script>
export default {
    props: {
        data: Array,    // The latest page of data
        submitting: Boolean,    // Is an API request for more data in-progress?

        // Must include pagination data: page, page_size, total. Example below
		filters: {
            type: Object,
            default: () => { return {
				page: undefined,
				page_size: undefined,
				total: undefined,
			} },
        },

		maxPages: {
			type: Number,
			default: 5,
		},
		scrollSensitivity: { // Lower values improve UX, higher values are useful if maxPages and page_size are both small to avoid page-looping.
			type: Number,
			default: 0.8, // E.g: 0.85 => page load triggered when scrolled > 85% up or down the component.
		},
		scrollElementId: { // Pass if you wish to scroll according to a specific component (defaults to main page scroll)
            type: String,
            default: 'main',
        },
    },
    mounted() {
		this.scrollElement = document.getElementById(this.scrollElementId)
		this.scrollElement.addEventListener('scroll', this.handleScroll)
	},
	updated() {
        // This makes sure the user can always scroll after a reset
		if (!this.submitting && this.$refs.rowElements && !this.canScroll()) this.scrollDown()
	},
	destroyed() {
		this.scrollElement.removeEventListener('scroll', this.handleScroll)
	},
    data() {
        return {
            rows: [],
            scrollElement: undefined,
			scrollData: {
				fromPage: 1,
				toPage: 1,
				append: true, // If true, user is scrolling down to next page, else user is scrolling up to previous page
				lastPageSize: 0,
				userScroll: true, // Set to false when scrolling programmatically to prevent handleScroll() from triggering
				reset: true, // Is set false only when a scroll pagination update is occurring
			},
			onPage: this.filters.page,
			scrollTop: 0,
        }
    },
    methods: {
        toPage: function (pageNo) {
            if (this.submitting) return
            this.$emit('toPage', pageNo)
        },
        scrollUp: function() {
			if (this.scrollData.fromPage === 1) return

			this.scrollData.append = false
			this.scrollData.reset = false
			this.scrollData.fromPage--

			this.toPage(this.scrollData.fromPage)
		},
		scrollDown: function() {
			if (this.lastPageLoaded) return

			this.scrollData.append = true
			this.scrollData.reset = false
			this.scrollData.toPage++

			this.toPage(this.scrollData.toPage)
		},
		handleScroll() {
			if (this.submitting) return

			if (!this.scrollData.userScroll) {
				this.scrollData.userScroll = true
				return
			}

			if (this.scrollFactorToBottom() > this.scrollSensitivity) this.scrollDown()
			else if (this.scrollFactorToTop() > this.scrollSensitivity) this.scrollUp()
		},

		/**
		 * So we can scroll without triggering pagination
		 *
		 * @param {number} x Horizontal scroll in px
		 * @param {number} y Vertical scroll in px
		 */
		programmaticScroll(y) {
			const toPos = this.scrollElement.scrollTop + y

			this.scrollData.userScroll = false
			this.scrollElement.scrollBy(0, y)

			// There's a strange bug which triggers a 2y scroll correction when scrolling up if and only if programmaticScroll is triggered when scrollTop is not near 0.
			// This watches for that and corrects it. It also acts as a general failsafe
			this.$nextTick(() => {
				if (this.scrollElement.scrollTop != toPos) {
					this.scrollData.userScroll = false
					this.scrollElement.scroll(this.scrollElement.scrollLeft, toPos)
				}
			})
		},

		/**
		 * Returns a decimal representing how close the user has scrolled to the bottom.
		 * E.g: 0.9 => scrolled down 90% of component's height.
		 */
		scrollFactorToBottom() {
			return (this.scrollElement.scrollTop + this.scrollElement.clientHeight) / this.scrollElement.scrollHeight
		},

		/**
		 * Returns a decimal representing how close the user has scrolled to the top.
		 * E.g: 0.9 => scrolled up 90% of component's height.
		 */
		scrollFactorToTop() {
			return (this.scrollElement.scrollHeight - this.scrollElement.scrollTop) / this.scrollElement.scrollHeight
		},

		/**
		 * Resets scroll-pagination settings
		 */
		resetScroll() {
			this.scrollData = {
				...this.scrollData,
				fromPage: 1,
				toPage: 1,
				append: true,
				lastPageSize: 0,
			}
		},

		/**
		 * Determines if it is possible for the user to scroll.
		 * We use this to load more data if the user cannot trigger a scroll-event.
		 */
		canScroll() {
			const curOverflow = this.scrollElement.style.overflow
			if ( !curOverflow || curOverflow === "visible" ) this.scrollElement.style.overflow = "hidden"

			const isOverflowing = this.scrollElement.clientHeight < this.scrollElement.scrollHeight
			this.scrollElement.style.overflow = curOverflow

			return isOverflowing;
		},
    },
    computed: {
        pageCount() {
			const count = Math.ceil(this.filters.total / this.filters.page_size)
            return count > 0 ? count : 1
        },
        lastPageLoaded() {
            return this.scrollData.toPage === this.pageCount
		},
    },
    watch: {
		data: {
            immediate: true,
            handler: function (newData) {
                // If true, data change is NOT pagination, so let's reset scrolling and data
                if (this.scrollData.reset) {
                    this.resetScroll()
                    this.rows = newData
                    return
                }
                else this.scrollData.reset = true	// Return to default value before continuing

                if (this.scrollData.append) {
                    this.rows.push(...newData)
                    if (this.lastPageLoaded) this.scrollData.lastPageSize = newData.length
                } else {
                    this.rows.unshift(...newData)
                }

                // If we've loaded the max no. of pages, we need to remove a page
                if (this.scrollData.toPage - this.scrollData.fromPage + 1 > this.maxPages) {

                    // If scrolling down, remove the first loaded page
                    if (this.scrollData.append) {
						this.rows.splice(0, this.filters.page_size)
                        this.scrollData.fromPage++

						let scrollUpBy = this.$refs.rowElements.slice(0, this.filters.page_size).reduce(
                            (total, el) => total + el.offsetHeight, 0
                        )

						this.programmaticScroll(-scrollUpBy)
					}

                    // Else scrolling up, so remove the last page
                    else {
                        const scrollByRows = this.lastPageLoaded ? this.scrollData.lastPageSize : this.filters.page_size

                        this.rows.splice(this.rows.length - scrollByRows, scrollByRows)
                        this.scrollData.toPage--

						let scrollDownBy = this.$refs.rowElements.slice(0, this.filters.page_size).reduce(
                            (total, el) => total + el.offsetHeight, 0
                        )

						this.programmaticScroll(scrollDownBy - this.$refs.skeletonUp.offsetHeight)
					}
                }

                // If a user loads a page then scrolls across to the other end of the results before the first requested page
                // has been returned by the API, the second handleScroll() call will have been blocked.
                // Let's check for this.
                this.$nextTick(() => {
                    if (!this.canScroll()) return

                    if (this.scrollFactorToBottom() === 1) this.scrollDown()
                    else if (this.scrollFactorToTop() === 1) this.scrollUp()
                })
            }
        },
		filters: function (newFilters, oldFilters) {
			Object.keys(newFilters).forEach(field => {
				if (field === 'page' || field === 'page_size' || field ===  'total') return

				if (newFilters[field] !== oldFilters[field]) {
					this.scrollData.userScroll = false
					this.scrollElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
				}
			})
		}
    },
}
</script>
